import {DEFAULT_CHAT_TOPIC} from '../constants/ChatModal.constants';
import {CHAT_TEXT} from '../constants/chatText.constants';
import {
  ConversationThread,
  HistoryApiMessage,
  PersistedChatHistory,
} from '../types/ChatModal.types';

import {createEmptyConversation, isChatTopic} from './chatConversation.util';
import {stripSensitiveChatText} from './chatSanitization.util';

export const normalizeHistoryTimestamp = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const milliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(milliseconds);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const trimmed = value.trim();
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      const milliseconds = numeric > 1e12 ? numeric : numeric * 1000;
      const date = new Date(milliseconds);
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString();
      }
    }

    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  return new Date().toISOString();
};

const normalizeHistoryTextCandidate = (value: string): string => {
  const trimmed = stripSensitiveChatText(value).trim();
  const unwrapped = trimmed.replace(/^['"]+|['"]+$/g, '').trim();
  const normalizedStatusToken = unwrapped
    .toLowerCase()
    .replace(/^[[({\s]+/, '')
    .replace(/[\])}\s]+$/, '')
    .trim();

  if (
    normalizedStatusToken === 'skipped' ||
    normalizedStatusToken === 'null' ||
    normalizedStatusToken === 'undefined'
  ) {
    return '';
  }

  return unwrapped;
};

const pickFirstMeaningfulText = (values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      const normalized = normalizeHistoryTextCandidate(value);
      if (normalized) {
        return normalized;
      }
    }
  }
  return '';
};

const extractHistoryTextFromSegments = (segments: unknown): string | null => {
  let normalizedSegments: unknown = segments;

  if (typeof normalizedSegments === 'string') {
    try {
      normalizedSegments = JSON.parse(normalizedSegments);
    } catch {
      normalizedSegments = null;
    }
  }

  if (!Array.isArray(normalizedSegments)) {
    return null;
  }

  const directTextParts = normalizedSegments
    .map(segment => {
      if (!segment || typeof segment !== 'object') {
        return null;
      }

      const typedSegment = segment as Record<string, unknown>;
      const segmentType =
        typeof typedSegment.type === 'string'
          ? typedSegment.type.toLowerCase()
          : '';
      const segmentText =
        typeof typedSegment.segment === 'string' ? typedSegment.segment : null;

      if (segmentType !== 'text' || !segmentText || !segmentText.trim()) {
        return null;
      }

      return stripSensitiveChatText(segmentText).trim();
    })
    .filter((value): value is string => Boolean(value));

  if (directTextParts.length > 0) {
    return directTextParts.join('\n\n');
  }

  const nestedTextParts = normalizedSegments
    .map(segment => {
      if (!segment || typeof segment !== 'object') {
        return null;
      }

      const typedSegment = segment as Record<string, unknown>;
      const nested = typedSegment.segment;

      if (!nested || typeof nested !== 'object') {
        return null;
      }

      const nestedSegment = nested as Record<string, unknown>;
      const nestedType =
        typeof nestedSegment.type === 'string'
          ? nestedSegment.type.toLowerCase()
          : '';
      const nestedText =
        typeof nestedSegment.segment === 'string'
          ? nestedSegment.segment
          : typeof nestedSegment.text === 'string'
          ? nestedSegment.text
          : null;

      if (nestedType !== 'text' || !nestedText || !nestedText.trim()) {
        return null;
      }

      return stripSensitiveChatText(nestedText).trim();
    })
    .filter((value): value is string => Boolean(value));

  if (nestedTextParts.length === 0) {
    return null;
  }

  return nestedTextParts.join('\n\n');
};

export const resolveHistoryMessageText = (message: HistoryApiMessage): string =>
  pickFirstMeaningfulText([
    message.text,
    message.message,
    message.content,
    message.response,
    extractHistoryTextFromSegments(message.segments),
  ]);

export const resolveHistoryMessageRole = (message: HistoryApiMessage): string =>
  pickFirstMeaningfulText([
    message.role,
    message.sender,
    message.author,
    message.type,
  ]).toLowerCase();

export const isUserHistoryRole = (role: string): boolean =>
  role === 'human' ||
  role === 'user' ||
  role === 'coach' ||
  role === 'client' ||
  role === 'customer';

const sanitizeConversation = (value: any): ConversationThread | null => {
  if (!value || typeof value !== 'object' || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    title:
      typeof value.title === 'string' && value.title.trim().length > 0
        ? value.title
        : CHAT_TEXT.newChat,
    topic: isChatTopic(value.topic) ? value.topic : DEFAULT_CHAT_TOPIC,
    messages: [],
    suggestions: Array.isArray(value.suggestions)
      ? value.suggestions.filter(
          (suggestion: any): suggestion is string =>
            typeof suggestion === 'string',
        )
      : [],
    hasUsedSuggestion: Boolean(value.hasUsedSuggestion),
    conversationId:
      typeof value.conversationId === 'string' ? value.conversationId : null,
    externalSessionId:
      typeof value.externalSessionId === 'string'
        ? value.externalSessionId
        : null,
    updatedAt:
      typeof value.updatedAt === 'string' && value.updatedAt.trim().length > 0
        ? value.updatedAt
        : new Date().toISOString(),
  };
};

export const sanitizePersistedHistory = (
  value: any,
): PersistedChatHistory | null => {
  if (
    !value ||
    typeof value !== 'object' ||
    !Array.isArray(value.conversations)
  ) {
    return null;
  }

  const conversations = value.conversations
    .map(sanitizeConversation)
    .filter(
      (
        conversation: ConversationThread | null,
      ): conversation is ConversationThread => Boolean(conversation),
    );

  if (conversations.length === 0) {
    return null;
  }

  const activeConversationId =
    typeof value.activeConversationId === 'string' &&
    conversations.some(
      (conversation: ConversationThread) =>
        conversation.id === value.activeConversationId,
    )
      ? value.activeConversationId
      : conversations[0]?.id ?? '';

  if (!activeConversationId) {
    return null;
  }

  return {
    activeConversationId,
    conversations,
  };
};

export const createFallbackConversationState = (): {
  activeConversationId: string;
  conversations: ConversationThread[];
} => {
  const fallbackConversation = createEmptyConversation(DEFAULT_CHAT_TOPIC);

  return {
    activeConversationId: fallbackConversation.id,
    conversations: [fallbackConversation],
  };
};
